import { useRouter } from "next/router";
import { ImpactShopSortOption, ImpactType, values } from "@ecologi/shared";
import {
  CategoryFilterValue,
  IMPACT_FUND_VALUE,
  ALL_CATEGORIES_VALUE,
  ALL_CARBON_VALUE,
} from "./impactShopFilters.constants";

export enum ImpactShopFilterType {
  IMPACT_TYPE = "impactType",
  LOCATION = "location",
}

export type ImpactShopFilter =
  | { type: ImpactShopFilterType.IMPACT_TYPE; value: CategoryFilterValue }
  | { type: ImpactShopFilterType.LOCATION; value: string };

export function useImpactShopFilter(): {
  selectedImpactTypes: CategoryFilterValue[];
  selectedLocations: string[];
  sortOption: ImpactShopSortOption;
  setSortOption: (sortOption: ImpactShopSortOption) => void;
  setFilter: (type: ImpactShopFilterType, values: string[]) => void;
  removeFilter: (filter: ImpactShopFilter | ImpactShopFilter[]) => void;
  clearAllFilters: () => void;
} {
  const router = useRouter();

  const impactTypes = parseImpactTypes(
    router.query[ImpactShopFilterType.IMPACT_TYPE]
  );
  const locations = parseLocations(router.query[ImpactShopFilterType.LOCATION]);
  const sortOption = parseSortOption(router.query.sortBy);

  const pushQuery = (query: typeof router.query) => {
    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    );
  };

  const setFilter = (type: ImpactShopFilterType, values: string[]) => {
    const query = { ...router.query };

    if (values.length > 0) {
      query[type] = values;
    } else {
      delete query[type];
    }

    pushQuery(query);
  };

  const setSortOption = (newSortOption: ImpactShopSortOption) => {
    const query = { ...router.query };

    if (newSortOption === ImpactShopSortOption.RECOMMENDED) {
      delete query.sortBy;
    } else {
      query.sortBy = newSortOption;
    }

    pushQuery(query);
  };

  const removeFilter = (filter: ImpactShopFilter | ImpactShopFilter[]) => {
    const filters = Array.isArray(filter) ? filter : [filter];

    const impactTypeFilters = filters.filter(
      (f) => f.type === ImpactShopFilterType.IMPACT_TYPE
    );
    const locationFilters = filters.filter(
      (f) => f.type === ImpactShopFilterType.LOCATION
    );

    if (impactTypeFilters.length > 0) {
      const valuesToRemove = impactTypeFilters.map((f) => f.value);
      const newImpactTypes = impactTypes.filter(
        (t) => !valuesToRemove.includes(t)
      );
      setFilter(ImpactShopFilterType.IMPACT_TYPE, newImpactTypes);
    }

    if (locationFilters.length > 0) {
      const valuesToRemove = locationFilters.map((f) => f.value);
      const newLocations = locations.filter(
        (loc) => !valuesToRemove.includes(loc)
      );
      setFilter(ImpactShopFilterType.LOCATION, newLocations);
    }
  };

  const clearAllFilters = () => {
    const query = { ...router.query };
    delete query[ImpactShopFilterType.IMPACT_TYPE];
    delete query[ImpactShopFilterType.LOCATION];
    pushQuery(query);
  };

  return {
    selectedImpactTypes: impactTypes,
    selectedLocations: locations,
    sortOption,
    setFilter,
    removeFilter,
    clearAllFilters,
    setSortOption,
  };
}

function parseImpactTypes(
  value: string | string[] | undefined
): CategoryFilterValue[] {
  if (!value) return [];

  const values = Array.isArray(value) ? value : [value];
  return values.filter(isCategoryFilterValue);
}

function parseSortOption(
  value: string | string[] | undefined
): ImpactShopSortOption {
  if (!value) {
    return ImpactShopSortOption.RECOMMENDED;
  }

  const sortValue = Array.isArray(value) ? value[0] : value;

  if (
    values(ImpactShopSortOption).includes(sortValue as ImpactShopSortOption)
  ) {
    return sortValue as ImpactShopSortOption;
  }

  return ImpactShopSortOption.RECOMMENDED;
}

function parseLocations(value: string | string[] | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function isCategoryFilterValue(value: string): value is CategoryFilterValue {
  return (
    values(ImpactType).includes(value as ImpactType) ||
    value === IMPACT_FUND_VALUE ||
    value === ALL_CATEGORIES_VALUE ||
    value === ALL_CARBON_VALUE
  );
}
